import { useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';
import RequestAccessModal from '../../components/billing/RequestAccessModal';

export default function SecureEvidenceSharingPage() {
  const [showRequestAccess, setShowRequestAccess] = useState(false);

  const faqs = [
    {
      question: 'Do auditors need a GitHub account to view evidence?',
      answer: 'No. Share links provide anonymous access to evidence packs without requiring GitHub authentication or EvidentTrail accounts.'
    },
    {
      question: 'What formats are available in shared evidence packs?',
      answer: 'Available formats depend on your plan tier. Early Access includes PDF, Starter adds CSV, Growth adds JSON and ZIP exports. All exports include tamper-evident checksums.'
    },
    {
      question: 'How long do share links remain active?',
      answer: 'Link expiry depends on your plan: 7 days for Early Access, 14 days for Starter, 30 days for Growth and Enterprise. You can revoke links manually at any time.'
    },
    {
      question: 'Can I revoke a share link after sending it?',
      answer: 'Yes. Share links can be revoked immediately through the evidence pack management interface, preventing further access even if the link has been distributed.'
    },
    {
      question: 'Are download limits enforced on share links?',
      answer: 'Yes. Early Access allows 25 downloads per link, Starter allows 250, and Growth/Enterprise plans have unlimited downloads within the expiry window.'
    }
  ];

  const longformSections = [
    {
      title: 'What auditors ask for',
      paragraphs: [
        'Auditors need read-only access to evidence without GitHub credentials or direct repository access. They require verifiable, tamper-evident exports that prove control operation during the audit window.'
      ],
      bullets: [
        'Anonymous access without account creation',
        'Time-limited links that expire automatically',
        'Tamper-evident checksums for integrity verification',
        'Export format flexibility (PDF for reports, CSV for sampling analysis)'
      ]
    },
    {
      title: 'Evidence artifacts included',
      paragraphs: [
        'Shared evidence packs bundle all captured metadata into verifiable exports. Each pack includes checksums and manifests for integrity verification.'
      ],
      bullets: [
        'Pull request approvals with reviewer identity and timestamp',
        'CI check results and required status checks',
        'Branch protection rule snapshots',
        'Exception records when controls were bypassed',
        'SHA-256 checksums and JSON manifests for verification'
      ]
    },
    {
      title: 'How EvidentTrail works (secure sharing)',
      paragraphs: [
        'Evidence packs are exported using token-based share links with configurable expiry and download limits. Format availability and watermarking are controlled by plan tier entitlements.'
      ],
      bullets: [
        'Token-based access (no authentication required)',
        'Automatic expiry enforcement (7/14/30 days based on plan)',
        'Format restrictions enforced by entitlements',
        'Download count tracking and limits',
        'Manual revocation support'
      ]
    },
    {
      title: 'How to interpret the output',
      paragraphs: [
        'Use evidence pack checksums to verify file integrity. Watermarks (when applied) indicate sharing source and timestamp. Review manifests to understand included evidence scope.'
      ],
      bullets: [
        'Verify SHA-256 checksum matches manifest',
        'Check watermark timestamp and source',
        'Review manifest.json for evidence scope and metadata',
        'Cross-reference approval timestamps with audit sampling period'
      ]
    },
    {
      title: 'Common failure modes',
      paragraphs: [
        'Share links can fail if expired, revoked, or if download limits are exceeded. Format restrictions may prevent access to CSV/JSON exports on lower-tier plans.'
      ],
      bullets: [
        'Link expired beyond plan retention window',
        'Share link manually revoked by owner',
        'Download limit exceeded (25/250 based on plan)',
        'Requested format not included in plan entitlements',
        'Evidence pack deleted due to retention policies'
      ]
    }
  ];

  return (
    <>
      <MarketingLayout
        title="Secure Evidence Sharing for Auditors | EvidentTrail"
        description="Share tamper-evident GitHub audit evidence with auditors using time-limited, format-controlled links. No GitHub account required. Automated expiry and download limits."
        h1="Secure Evidence Sharing"
        heroText="Give auditors direct access to GitHub change control evidence without granting repository permissions."
        type="article"
      >
        <BreadcrumbJsonLd
          items={[
            { name: 'Home', url: 'https://evidenttrail.com/' },
            { name: 'Secure Evidence Sharing', url: 'https://evidenttrail.com/secure-evidence-sharing' }
          ]}
        />
        <FaqJsonLd items={faqs} />

        {/* How Evidence Sharing Works */}
        <section className={tokens.sectionAlt}>
          <div className={tokens.container}>
            <div className="max-w-3xl mx-auto">
              <h2 className={`${tokens.heading.h2} mb-6`}>How Evidence Sharing Works</h2>
              <p className={`${tokens.text.base} mb-6`}>
                EvidentTrail generates share links for evidence packs, providing anonymous access to audit evidence without requiring GitHub accounts or repository permissions. Each link is token-based, time-limited, and format-controlled.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className={tokens.card}>
                  <h3 className={`${tokens.heading.h3} mb-3`}>Guest Mode (Anonymous)</h3>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>✅ No account creation required</li>
                    <li>✅ Single evidence pack per session</li>
                    <li>✅ 24-hour retention window</li>
                    <li>✅ PDF, CSV, JSON export formats</li>
                    <li>⚠️ No download limits (browser download only)</li>
                  </ul>
                </div>

                <div className={tokens.card}>
                  <h3 className={`${tokens.heading.h3} mb-3`}>Authenticated Sharing</h3>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li>✅ Share links with configurable expiry</li>
                    <li>✅ Format restrictions by plan tier</li>
                    <li>✅ Download limits (25/250/unlimited)</li>
                    <li>✅ Watermarking (plan-dependent)</li>
                    <li>✅ Manual revocation support</li>
                  </ul>
                </div>
              </div>

              <h3 className={`${tokens.heading.h3} mb-4`}>Share Link Entitlements by Plan</h3>
              <div className={tokens.card}>
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-700">
                    <tr className="text-left">
                      <th className="py-2 text-white">Feature</th>
                      <th className="py-2 text-white">Early Access</th>
                      <th className="py-2 text-white">Starter</th>
                      <th className="py-2 text-white">Growth+</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    <tr className="border-b border-slate-800">
                      <td className="py-2">Link Expiry</td>
                      <td>7 days</td>
                      <td>14 days</td>
                      <td>30 days</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-2">Formats</td>
                      <td>PDF only</td>
                      <td>PDF, CSV</td>
                      <td>PDF, CSV, JSON, ZIP</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-2">Downloads</td>
                      <td>25 per link</td>
                      <td>250 per link</td>
                      <td>Unlimited</td>
                    </tr>
                    <tr>
                      <td className="py-2">Revocation</td>
                      <td>✅ Supported</td>
                      <td>✅ Supported</td>
                      <td>✅ Supported</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className={`${tokens.text.small} text-slate-400 italic mt-4`}>
                Guest mode (anonymous) provides 24-hour retention with PDF/CSV/JSON access but no download limits or watermarking.
              </p>
            </div>
          </div>
        </section>

        <SeoLongformSections sections={longformSections} faqs={faqs} />

        {/* Internal Linking */}
        <section className={tokens.section}>
          <div className={tokens.container}>
            <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related Evidence Topics</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/auditor-self-serve" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Auditor Self-Serve</h4>
                <p className="text-sm text-slate-400">Guest mode for anonymous evidence exports.</p>
              </Link>
              <Link to="/continuous-audit-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Continuous Audit Evidence</h4>
                <p className="text-sm text-slate-400">Ongoing evidence capture for compliance.</p>
              </Link>
              <Link to="/read-only-github-audit" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Read-Only Audits</h4>
                <p className="text-sm text-slate-400">Secure evidence collection without write access.</p>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={tokens.sectionAlt}>
          <div className={tokens.container}>
            <div className="max-w-2xl mx-auto text-center">
              <h2 className={`${tokens.heading.h2} mb-4`}>Start Sharing Evidence Securely</h2>
              <p className={`${tokens.text.base} mb-8`}>
                Request access to try evidence pack sharing with your auditors.
              </p>
              <button 
                onClick={() => setShowRequestAccess(true)}
                className="btn btn-primary text-lg px-8 py-3"
              >
                Request Access
              </button>
            </div>
          </div>
        </section>
      </MarketingLayout>

      <RequestAccessModal 
        isOpen={showRequestAccess}
        onClose={() => setShowRequestAccess(false)}
        preselectedFeature="Evidence Pack Sharing"
      />
    </>
  );
}
